/**
 * Klaviyo Back-in-Stock (BIS) Controller & Web Component
 * Manages variant-level subscriptions using Klaviyo's native Client Back-in-Stock API.
 */

/**
 * Helper to discover the Klaviyo Company ID (Public API Key / Site ID).
 * Searches Theme Setting, global Klaviyo objects, and injected app embed scripts.
 * @returns {string | null}
 */
export function getKlaviyoCompanyId() {
  if (window.NEIWAI_KLAVIYO_PUBLIC_KEY && typeof window.NEIWAI_KLAVIYO_PUBLIC_KEY === 'string' && window.NEIWAI_KLAVIYO_PUBLIC_KEY.trim()) {
    return window.NEIWAI_KLAVIYO_PUBLIC_KEY.trim();
  }

  if (window.klaviyoCompanyId && typeof window.klaviyoCompanyId === 'string' && window.klaviyoCompanyId.trim()) {
    return window.klaviyoCompanyId.trim();
  }

  if (window._learnq && typeof window._learnq.account === 'string' && window._learnq.account.trim()) {
    return window._learnq.account.trim();
  }

  // Scan scripts for company_id parameter (Shopify Klaviyo App Embed)
  const scripts = document.querySelectorAll('script[src*="klaviyo.js"], script[src*="onsite.js"]');
  for (const script of scripts) {
    const src = script.getAttribute('src') || '';
    const match = src.match(/[?&]company_id=([a-zA-Z0-9_-]+)/);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

export class BackInStockModal extends HTMLElement {
  /** @type {HTMLElement | null} */
  #formView = null;
  /** @type {HTMLElement | null} */
  #successView = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #emailInput = null;
  /** @type {HTMLInputElement | null} */
  #variantInput = null;
  /** @type {HTMLElement | null} */
  #fieldError = null;
  /** @type {HTMLElement | null} */
  #generalError = null;
  /** @type {HTMLButtonElement | null} */
  #submitBtn = null;
  /** @type {HTMLElement | null} */
  #btnText = null;
  /** @type {HTMLElement | null} */
  #btnLoading = null;
  /** @type {HTMLElement | null} */
  #variantDisplay = null;
  /** @type {HTMLImageElement | null} */
  #productImage = null;
  /** @type {HTMLElement | null} */
  #confirmedEmail = null;
  /** @type {HTMLElement | null} */
  #confirmedVariant = null;

  connectedCallback() {
    this.#formView = this.querySelector('[data-bis-form-view]');
    this.#successView = this.querySelector('[data-bis-success-view]');
    this.#form = this.querySelector('[data-bis-form]');
    this.#emailInput = this.querySelector('[data-bis-email-input]');
    this.#variantInput = this.querySelector('[data-bis-variant-input]');
    this.#fieldError = this.querySelector('[data-bis-field-error]');
    this.#generalError = this.querySelector('[data-bis-general-error]');
    this.#submitBtn = this.querySelector('[data-bis-submit]');
    this.#btnText = this.querySelector('.bis-modal__btn-text');
    this.#btnLoading = this.querySelector('.bis-modal__btn-loading');
    this.#variantDisplay = this.querySelector('[data-bis-variant-display]');
    this.#productImage = this.querySelector('[data-bis-image]');
    this.#confirmedEmail = this.querySelector('[data-bis-confirmed-email]');
    this.#confirmedVariant = this.querySelector('[data-bis-confirmed-variant]');

    // Attach close button and overlay handlers
    this.querySelectorAll('[data-bis-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Form submission
    this.#form?.addEventListener('submit', this.#handleSubmit.bind(this));

    // Clear validation error on input
    this.#emailInput?.addEventListener('input', () => {
      this.#clearErrors();
    });

    // ESC key listener
    document.addEventListener('keydown', this.#handleKeydown.bind(this));

    // Variant change listener (ThemeEvents.variantUpdate)
    document.addEventListener('variant:update', this.#handleVariantUpdate.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#handleKeydown.bind(this));
    document.removeEventListener('variant:update', this.#handleVariantUpdate.bind(this));
  }

  /**
   * Updates the modal's targeted variant
   * @param {string|number} variantId
   * @param {string} variantTitle
   * @param {string} [imageUrl]
   */
  updateVariant(variantId, variantTitle, imageUrl) {
    if (variantId) {
      this.dataset.variantId = String(variantId);
      if (this.#variantInput) {
        this.#variantInput.value = String(variantId);
      }
    }
    if (variantTitle) {
      this.dataset.variantTitle = variantTitle;
      if (this.#variantDisplay) {
        this.#variantDisplay.textContent = variantTitle;
      }
    }
    if (imageUrl && this.#productImage) {
      this.#productImage.src = imageUrl;
    }
  }

  /**
   * Opens the modal
   * @param {object} [variantData]
   */
  open(variantData = {}) {
    if (variantData.variantId) {
      this.updateVariant(variantData.variantId, variantData.variantTitle, variantData.imageUrl);
    }

    this.#resetViews();
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus email input after transition
    setTimeout(() => {
      this.#emailInput?.focus();
    }, 100);
  }

  /**
   * Closes the modal
   */
  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  #handleKeydown(event) {
    if (event.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
      this.close();
    }
  }

  /**
   * Listens to variant:update dispatched by variant-picker
   */
  #handleVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    const imageUrl = variant.featured_media?.preview_image?.src
      ? `${variant.featured_media.preview_image.src}&width=140`
      : undefined;

    this.updateVariant(variant.id, variant.title, imageUrl);
  }

  #resetViews() {
    if (this.#formView) this.#formView.style.display = 'block';
    if (this.#successView) this.#successView.style.display = 'none';
    this.#clearErrors();
    this.#setLoading(false);
  }

  #clearErrors() {
    if (this.#fieldError) {
      this.#fieldError.textContent = '';
      this.#fieldError.style.display = 'none';
    }
    if (this.#generalError) {
      this.#generalError.textContent = '';
      this.#generalError.style.display = 'none';
    }
    this.#emailInput?.classList.remove('is-invalid');
  }

  #setLoading(isLoading) {
    if (this.#submitBtn) {
      this.#submitBtn.disabled = isLoading;
    }
    if (this.#btnText) {
      this.#btnText.style.display = isLoading ? 'none' : 'inline';
    }
    if (this.#btnLoading) {
      this.#btnLoading.style.display = isLoading ? 'flex' : 'none';
    }
  }

  /**
   * Handles Back-in-Stock form submission to Klaviyo Client API
   */
  async #handleSubmit(event) {
    event.preventDefault();
    this.#clearErrors();

    const email = this.#emailInput?.value?.trim() || '';
    const variantId = this.#variantInput?.value || this.dataset.variantId || '';
    const variantTitle = this.dataset.variantTitle || '';

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      if (this.#fieldError) {
        this.#fieldError.textContent = 'Please enter a valid email address.';
        this.#fieldError.style.display = 'block';
      }
      this.#emailInput?.classList.add('is-invalid');
      this.#emailInput?.focus();
      return;
    }

    if (!variantId) {
      if (this.#generalError) {
        this.#generalError.textContent = 'No variant selected. Please select a variant and try again.';
        this.#generalError.style.display = 'block';
      }
      return;
    }

    const companyId = getKlaviyoCompanyId();
    if (!companyId) {
      console.warn('[Klaviyo BIS] Klaviyo Company ID / Public Key not found. Please configure it in Theme Settings > Klaviyo.');
      if (this.#generalError) {
        this.#generalError.textContent = 'Notification service is temporarily unavailable. Please check back soon.';
        this.#generalError.style.display = 'block';
      }
      return;
    }

    this.#setLoading(true);

    try {
      // Klaviyo Client Back-in-Stock Subscription API
      const endpoint = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`;
      
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'revision': '2024-10-15',
        },
        body: JSON.stringify(payload),
      });

      // Klaviyo responds with 202 Accepted on success
      if (response.ok || response.status === 202 || response.status === 200 || response.status === 201) {
        // Sync profile identity if Klaviyo onsite is loaded
        if (window._learnq && Array.isArray(window._learnq)) {
          window._learnq.push(['identify', { $email: email }]);
        }
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify({ email: email });
        }

        // Display Success View
        if (this.#confirmedEmail) {
          this.#confirmedEmail.textContent = email;
        }
        if (this.#confirmedVariant) {
          this.#confirmedVariant.textContent = variantTitle || 'this item';
        }

        if (this.#formView) this.#formView.style.display = 'none';
        if (this.#successView) this.#successView.style.display = 'block';
      } else {
        const errorData = await response.json().catch(() => null);
        const errorMsg = errorData?.errors?.[0]?.detail || 'Unable to subscribe. Please verify your details and try again.';
        if (this.#generalError) {
          this.#generalError.textContent = errorMsg;
          this.#generalError.style.display = 'block';
        }
      }
    } catch (err) {
      console.error('[Klaviyo BIS] Subscription error:', err);
      if (this.#generalError) {
        this.#generalError.textContent = 'Network error. Please check your connection and try again.';
        this.#generalError.style.display = 'block';
      }
    } finally {
      this.#setLoading(false);
    }
  }
}

// Define custom element if not already registered
if (!customElements.get('back-in-stock-modal')) {
  customElements.define('back-in-stock-modal', BackInStockModal);
}

/**
 * Global trigger helper: Opens Back-in-Stock modal for specified variant.
 * @param {object} options
 * @param {string|number} [options.variantId]
 * @param {string} [options.variantTitle]
 * @param {string} [options.imageUrl]
 */
export function openBackInStockModal(options = {}) {
  const modal = document.getElementById('BackInStockModal') || document.querySelector('back-in-stock-modal');
  if (modal && typeof modal.open === 'function') {
    modal.open(options);
  } else {
    console.warn('[Klaviyo BIS] BackInStockModal element not found in DOM.');
  }
}

// Global click delegate for any button with [data-bis-trigger] or .back-in-stock-button
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-bis-trigger], .back-in-stock-button') : null;
  if (!trigger) return;

  event.preventDefault();

  const variantId = trigger.getAttribute('data-variant-id') || '';
  const variantTitle = trigger.getAttribute('data-variant-title') || '';
  const imageUrl = trigger.getAttribute('data-product-image') || '';

  openBackInStockModal({ variantId, variantTitle, imageUrl });
});
